import { Features } from 'generated/featureDefinitions'
import {
  ExpensesListPageDocument,
  ExpensesListPageQuery,
  ExpensesListProjectsDocument,
  ExpensesListProjectsQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

type ApiCoreCurrency = NonNullable<ExpensesListPageQuery['apiCore']['currencies']>[number]

export type ExpensePageProject = NonNullable<
  ExpensesListProjectsQuery['projectManagement']['projects']
>[number]

export type ExpensePageSubproject = NonNullable<
  ExpensesListProjectsQuery['projectManagement']['subprojects']
>[number]

export type Currency = ApiCoreCurrency

interface UseExpensesListPage {
  companyId: number
  skipProjectsQuery?: boolean
}

export const useExpensesListPage = ({
  companyId,
  skipProjectsQuery = false,
}: UseExpensesListPage) => {
  const { loading, data, error } = useQuery(ExpensesListPageDocument, {
    variables: {
      companyIds: [companyId],
      featureAvailabilitiesIds: [
        Features.projects_management,
        Features.projects_with_subprojects,
        Features.expenses_report_on_behalf,
        Features.expenses_per_diems,
      ],
    },
  })
  const hasProjectsManagement =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id == Features.projects_management
    )?.available ?? false
  const hasSubprojectsManagement =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id == Features.projects_with_subprojects
    )?.available ?? false
  const hasProjectsFeature = hasProjectsManagement && hasSubprojectsManagement
  const hasExpensesReportOnBehalf =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id == Features.expenses_report_on_behalf
    )?.available ?? false
  const canUsePerDiems =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id == Features.expenses_per_diems
    )?.available ?? false

  const hasExpensesPerDiems =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id == Features.expenses_per_diems
    )?.available ?? false

  const canCreateExpenses = data?.expenses?._policies.expenseCreateExpenses.can ?? false
  const { loading: loadingProjects, data: projectsData } = useQuery(ExpensesListProjectsDocument, {
    skip: !hasProjectsFeature || skipProjectsQuery,
  })

  const recommendationsEnabled = data?.permissions?.contextlessPolicyResolutions?.[0]?.can ?? false

  return {
    error,
    loading: loading || loadingProjects,
    categories: data?.finance?.categories.nodes ?? [],
    subcategories: data?.finance?.subcategories.nodes ?? [],
    currencies: data?.apiCore?.currencies ?? [],
    projects: projectsData?.projectManagement?.projects ?? [],
    subprojects: projectsData?.projectManagement?.subprojects ?? [],
    canSeeTransactions: data?.banking?._policies?.bankAccountRead?.can ?? false,
    hasExpensesReportOnBehalfFeature: hasExpensesReportOnBehalf,
    hasExpensesPerDiemsFeature: hasExpensesPerDiems,
    canUsePerDiems: canUsePerDiems,
    canCreateExpenses: canCreateExpenses,
    recommendationsEnabled: recommendationsEnabled,
  }
}
